import React from 'react';
import DOMPurify from 'dompurify';
import { AIAssistantMessage } from '../../types/organizations';

interface AIAssistantModalProps {
  message: AIAssistantMessage;
  onClose: () => void;
}

const AIAssistantModal: React.FC<AIAssistantModalProps> = ({ message, onClose }) => {
  if (!message.metadata) return null;

  const { reasoning, data_sources, confidence } = message.metadata;

  const renderMessage = (content: string) => {
    // First, unescape common escape sequences from the JSON string
    // Handle \n, \$, \-, etc. before HTML escaping
    let text = content
      .replace(/\\n/g, '\n')  // Convert \n to actual newlines
      .replace(/\\\$/g, '$')  // Remove unnecessary escaping of $
      .replace(/\\-/g, '-')   // Remove unnecessary escaping of -
      .replace(/\\\*/g, '*')  // Remove unnecessary escaping of *
      .replace(/\\:/g, ':');  // Remove unnecessary escaping of :
    
    // HTML-escape the content to protect special characters like <, >, &
    const escapeHtml = (str: string) => {
      return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    };
    
    let html = escapeHtml(text);
    
    // Convert newlines to <br /> tags (after escaping)
    html = html.replace(/\n/g, '<br />');
    
    // Apply markdown conversions on escaped text
    // Bold: *text* (primary), also support legacy **text** and __text__
    // Note: asterisks are already unescaped at this point, so match plain * not \*
    html = html.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<strong>$1</strong>');
    html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/__([^_]+)__/g, '<strong>$1</strong>');
    
    // Italic: _text_ only (single underscores)
    html = html.replace(/(?<!_)_([^_]+)_(?!_)/g, '<em>$1</em>');
    
    // Sanitize HTML to prevent XSS attacks
    const sanitized = DOMPurify.sanitize(html, {
      ALLOWED_TAGS: ['strong', 'em', 'br', 'b', 'i'],
      ALLOWED_ATTR: []
    });
    
    return <span dangerouslySetInnerHTML={{ __html: sanitized }} />;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <img 
              src="/ARiA-Logo-PrimaryLightBlue-Transparent-BG.png" 
              alt="ARiA Logo" 
              className="w-8 h-8"
            />
            <h3 className="text-lg font-semibold text-gray-900">Response Details from ARiA</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(80vh-120px)]">
          <div className="space-y-6">
            {/* Original Question */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">Question</h4>
              <div className="bg-blue-50 rounded-lg p-4 text-sm text-blue-900">
                {message.metadata?.question || 'Question not available'}
              </div>
            </div>

            {/* Original Answer */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">Answer</h4>
              <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-700">
                {renderMessage(message.content)}
              </div>
            </div>

            {/* Confidence */}
            {confidence && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">Confidence Level</h4>
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center space-x-2">
                    <div className="flex-1">
                      <div className="text-sm text-blue-800">{confidence}</div>
                    </div>
                    <div className="flex items-center space-x-1">
                      {confidence.toLowerCase().includes('high') && (
                        <>
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          <span className="text-xs text-green-600">High</span>
                        </>
                      )}
                      {confidence.toLowerCase().includes('medium') && (
                        <>
                          <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                          <span className="text-xs text-yellow-600">Medium</span>
                        </>
                      )}
                      {confidence.toLowerCase().includes('low') && (
                        <>
                          <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                          <span className="text-xs text-red-600">Low</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Data Sources */}
            {data_sources && data_sources.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">Data Sources Used</h4>
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex flex-wrap gap-2">
                    {data_sources.map((source, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
                      >
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {source}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Reasoning Steps */}
            {reasoning && reasoning.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">Analysis Steps</h4>
                <div className="space-y-3">
                  {reasoning.map((step, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                          <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-medium">
                            {index + 1}
                          </div>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-gray-700">{step}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Timestamp */}
            <div className="text-xs text-gray-500 text-center pt-4 border-t border-gray-200">
              Generated at {message.timestamp.toLocaleString()}
            </div>
          </div>
        </div>

        
      </div>
    </div>
  );
};

export default AIAssistantModal;
